/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2016-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
*
* A small portion of this code is from angular-medium-editor: https://github.com/thijsw/angular-medium-editor
*/

/**
* A directive modifies an element already annotated with the medium editor clone to also use the
* medium-editor-insert-plugin.
*
* A few other modifications to make the editor work nicely within bulb have been added as well.
*/
angular.module('bulbApp.contentEditorModule')
  .directive('contentEditor', [
    '$translate',
    'AeroDialogService',
    'AssetUploadEventService',
    'AssetUploadHttpService',
    'BulbBusyService',
    'BulbImageCarouselService',
    'BulbInsertBlockExtensionFactory',
    'BulbCommentService',
    'BulbCKEStateService',
    'ASSET_LIBRARY',
    'Restangular',
    'SerialSaveService',
    'UnitStateStore',
    'UppyDashboardService',
    'CONFIG',
    '$window',
    'CONTENT_EDITOR',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    contentEditor]);

function contentEditor(
  $translate,
  AeroDialogService,
  AssetUploadEventService,
  AssetUploadHttpService,
  BulbBusyService,
  BulbImageCarouselService,
  BulbInsertBlockExtensionFactory,
  BulbCommentService,
  BulbCKEStateService,
  ASSET_LIBRARY,
  Restangular,
  SerialSaveService,
  UnitStateStore,
  UppyDashboardService,
  CONFIG,
  $window,
  CONTENT_EDITOR,
) {
  return {
    priority: 999,
    restrict: 'A',
    controller: 'ContentEditorController as contentEditorCtrl',
    link: function link(scope, elem, attr) {
      // We are still grabbing the content editor controller from the scope to have access to the current page
      // The user is on -- this is needed to successfully make REST calls to the server that create and update
      // Blocks [image,video,audio, etc...]
      const { contentEditorCtrl } = scope;
      contentEditorCtrl.page = scope.$eval(attr.bulbPage);

      // Initializing initial rotation values :
      let rotationDegree = ASSET_LIBRARY.ROTATE.INITIAL_START_DEGREE;
      const unmodifiedRotationDegree = ASSET_LIBRARY.ROTATE.INITIAL_START_DEGREE;

      // Initialize our 'insertBlockExtension' so that we have access to the media button workflows. i.e. insert
      // Image, video, audio, etc...
      const insertBlockExtension = new BulbInsertBlockExtensionFactory.CreateExtension({
        scope,
        contentEditorCtrl,
      });

      // Initialize our 'insertBlockExtension' which in turn initializes the workflow:
      insertBlockExtension.init();

      /**
       * All events that will handle CKEditor5 asset block creation.
       */
      $window.addEventListener('openAssetLibraryImageType', handleImageButton, false);
      $window.addEventListener('openAssetLibraryFileType', handleFileAttachmentButton, false);
      $window.addEventListener('openAssetLibraryVideoType', handleVideoButton, false);
      $window.addEventListener('openAssetLibraryAudioType', handleAudioButtonClick, false);
      $window.addEventListener('openEmbedUrlModal', handleEmbedButtonClick, false);
      $window.addEventListener('urlPasteEvent', handleURLPaste, false);

      /**
       * Event to handle the cropping of an image:
       */
      $window.addEventListener('bulbCropImage', handleImageCrop, false);

      /**
       * Event to handle the rotation of an image:
       */
      $window.addEventListener('bulbRotateImage', handleImageRotation, false);

      /**
       * Event to handle reverting the image carousel back to an image block.
       */
      $window.addEventListener('revertCarouselToImage', handleRevertingImageCarousel, false);

      /**
       * Event to handle comment in ckeditor.
       */
      $window.addEventListener('openInlineCommentBox', handleInlineCommentBox, false);

      /**
       * ###################### One-Time event listeners ########################
       */
      function handleImageButton(event) {
        insertBlockExtension.handleMediaButtonClick(event);
      }

      function handleFileAttachmentButton(event) {
        insertBlockExtension.handleAttachmentButtonClick(event);
      }

      function handleVideoButton(event) {
        insertBlockExtension.handleVideoButtonClick(event);
      }

      function handleAudioButtonClick(event) {
        insertBlockExtension.handleAudioButtonClick(event);
      }

      function handleEmbedButtonClick(event) {
        insertBlockExtension.handleEmbedButtonClick(event);
      }

      function handleURLPaste(event) {
        insertBlockExtension.handleURLPaste(event);
      }

      function handleImageCrop(event) {
        const { blockId } = event.detail;
        const { imagePatch } = event.detail;

        // Grab the jQuery image element.
        const blockPath = isUndefinedProperty(imagePatch.selectedImageIndex) ? 'IMAGE' : 'IMAGE_CAROUSEL';
        const contentBlock = contentEditorCtrl.page.unit.contentBlocks[blockId];

        SerialSaveService.save({
          showBusy: true,
          save: () => Restangular.one('units', contentEditorCtrl.page.unit.id)
            .one(CONTENT_EDITOR.BLOCK_PATHS[blockPath], blockId).patch(imagePatch)
            .then((modifiedContentBlock) => {
              // Update the controllers content-block content:
              angular.copy(modifiedContentBlock.plain(), contentBlock);

              const bulbCompletedImageCrop = new CustomEvent('imageCropComplete', {
                detail: {
                  contentBlock: modifiedContentBlock.plain(),
                  currentEditor: event.detail.editor,
                },
              });

              // Update share status to reflect that the page has been edited:
              const ShareTransition = UnitStateStore.getShareTransitionEnum();
              UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

              // Dispatch the event to cke, so it can handle updating the model data:
              $window.dispatchEvent(bulbCompletedImageCrop);
            }),
        });
      }

      /**
       * Function reference that handles rotation of an image.
       *
       * @param event
       */
      function handleImageRotation(event) {
        const { blockId } = event.detail;
        const { imageIndex } = event.detail;
        const blockPath = isUndefinedProperty(imageIndex) ? 'IMAGE' : 'IMAGE_CAROUSEL';

        // Update the controllers content-block content:
        const contentBlock = contentEditorCtrl.page.unit.contentBlocks[blockId];

        // Grab the jQuery figure element.
        const $figureElement = (imageIndex)
          ? BulbImageCarouselService.findElementByIndexAndId(imageIndex, blockId)
          : jQuery(`figure[bulb-block-id="${blockId}"]`);

        // Grab the jQuery image element.
        const $imageElement = (imageIndex)
          ? BulbImageCarouselService.findElementByIndexAndId(imageIndex, blockId)
          : jQuery(`img[image-id="${blockId}"]`);

        // Start the rotation:
        rotationDegree += ASSET_LIBRARY.ROTATE.CONSTANT_ROTATION_DEGREE;

        const rotateImagePatch = {
          rotationDegree: (rotationDegree % 360),
          unmodifiedRotationDegree,
        };

        if (!isUndefinedProperty(imageIndex)) {
          rotateImagePatch.selectedImageIndex = parseInt(imageIndex, 10);
        }

        SerialSaveService.save({
          showBusy: true,
          save: () => Restangular.one('units', contentEditorCtrl.page.unit.id)
            .one(CONTENT_EDITOR.BLOCK_PATHS[blockPath], contentBlock.id)
            .patch(rotateImagePatch).then((modifiedContentBlock) => {
              angular.copy(modifiedContentBlock.plain(), contentBlock);
              if (blockPath === 'IMAGE') {
                const displayedImageAsset = contentBlock.scaledImageAsset || contentBlock.fullImageAsset;
                // Update the src for the image that was rotated :
                //   $imageElement.attr('src', displayedImageAsset.url);
              }

              /*
               * Need to reset the values of counter and rotationDegree after image has
               * been saved.
               */
              rotationDegree = ASSET_LIBRARY.ROTATE.INITIAL_START_DEGREE;

              /*
               * This is needed to re-initialize jquery-rotate. Without it, when the user presses the
               * rotate button, jquery-rotate won't register it as an animated click.
               */
              $figureElement.rotate({
                angle: rotationDegree,
                animateTo: rotationDegree,
              });

              // Update share status to reflect that the page has been edited:
              const ShareTransition = UnitStateStore.getShareTransitionEnum();
              UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

              // Dispatch the event:
              const bulbCompletedImageRotation = new CustomEvent('imageRotationComplete', {
                detail: {
                  contentBlock,
                  currentEditor: event.detail.editor,
                },
              });
              $window.dispatchEvent(bulbCompletedImageRotation);
            }),
        });
      }

      function handleRevertingImageCarousel(event) {
        const { blockId } = event.detail;
        const { unit } = contentEditorCtrl.page;

        // Re-create the original image block:
        const uploadImageCarouselBlockDTO = {
          imageCarouselBlockId: blockId,
          selectedImageIndex: event.detail.imageIndex,
          deleteOriginal: 'true',
        };

        SerialSaveService.save({
          showBusy: true,
          save: () => Restangular.one('units', unit.id).one('revert')
            .customPOST(uploadImageCarouselBlockDTO, CONTENT_EDITOR.BLOCK_PATHS.IMAGE_CAROUSEL)
            .then((response) => {
              angular.copy(response.contentBlock, unit.contentBlocks[response.contentBlock.id]);

              // Remove the carousel block from the array
              delete unit.contentBlocks[blockId];

              // Update share status to reflect that the page has been edited:
              const ShareTransition = UnitStateStore.getShareTransitionEnum();
              UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

              const carouselRevertedEvent = new CustomEvent('imageCarouselSuccessfullyReverted', {
                detail: {
                  contentBlock: response.contentBlock,
                  page: contentEditorCtrl.page,
                },
              });
              $window.dispatchEvent(carouselRevertedEvent);
            }),
        });
      }

      function handleInlineCommentBox(event) {
        BulbCommentService.createCommentThread('', '', '', event.detail);
      }

      /**
       * Need to destroy the listeners upon page redirection.
       */
      scope.$on('$destroy', () => {
        $window.removeEventListener('openAssetLibraryImageType', handleImageButton);
        $window.removeEventListener('openAssetLibraryFileType', handleFileAttachmentButton);
        $window.removeEventListener('openAssetLibraryVideoType', handleVideoButton);
        $window.removeEventListener('openAssetLibraryAudioType', handleAudioButtonClick);
        $window.removeEventListener('openEmbedUrlModal', handleEmbedButtonClick);
        $window.removeEventListener('urlPasteEvent', handleURLPaste);
        $window.removeEventListener('bulbCropImage', handleImageCrop);
        $window.removeEventListener('bulbRotateImage', handleImageRotation);
        $window.removeEventListener('revertCarouselToImage', handleRevertingImageCarousel);
        $window.removeEventListener('openInlineCommentBox', handleInlineCommentBox);
      });

      BulbCommentService.init(contentEditorCtrl.page, elem, '', scope, 'ckeditor');
    },

  };
}

/**
 * Helper function to determine if a property is undefined.
 *
 * @param prop Property value to check.
 * @returns {boolean} Indicating the property is undefined.
 */
function isUndefinedProperty(prop) {
  return (prop === null || typeof prop === 'undefined');
}
